import React, { useState, useContext, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import type { StagedProduct } from '@/stores/stagedProducts/stagedProducts.types';
import type { ProductModifier, SelectedProductModifier } from '@/types';
import { useProductModifiers } from '@/hooks/useProductModifiers';
import { useProductModifierCategories } from '@/hooks/useProductModifierCategories';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useCurrency } from '@/hooks/useCurrency';
import { useIvaPricing } from '@/hooks/useIvaPricing';
import { FEATURE_FLAGS } from '@/types/featureFlags';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { lineUnitPrice, toggleSelectedModifier } from '@/lib/productModifiers';
import { hasPositiveWeight } from '@/utils/weightPricing';

const UNCATEGORIZED_CATEGORY_ID = 'uncategorized';

interface ModifierNotesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  stagedProduct: StagedProduct | null;
  onSave: (lineId: string, notes: string, modifiers?: SelectedProductModifier[]) => void;
}

export const ModifierNotesModal: React.FC<ModifierNotesModalProps> = ({
  isOpen,
  onOpenChange,
  stagedProduct,
  onSave,
}) => {
  const { translator } = useContext(LanguageProviderContext);
  const { productModifiers } = useProductModifiers();
  const { categories, isLoading: categoriesLoading, error: categoriesError } = useProductModifierCategories();
  const { isEnabled } = useFeatureFlag();
  const { formatAmount } = useCurrency();
  const { showIvaPrice, getPriceWithIva } = useIvaPricing();
  const [localNotes, setLocalNotes] = useState('');
  const [localModifiers, setLocalModifiers] = useState<SelectedProductModifier[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [prevLineId, setPrevLineId] = useState<string | undefined | null>(stagedProduct?.lineId);

  // Sync state during render (React recommended way to avoid effect cascades)
  if (isOpen !== prevIsOpen || stagedProduct?.lineId !== prevLineId) {
    setPrevIsOpen(isOpen);
    setPrevLineId(stagedProduct?.lineId);
    setSelectedCategoryId(null);
    if (isOpen && stagedProduct) {
      setLocalNotes(stagedProduct.notes || '');
      setLocalModifiers(stagedProduct.modifiers ?? []);
    }
  }

  if (!stagedProduct) return null;

  // Promo lines (bundle pricing) and weight lines (price scales with kg) keep text-only modifiers.
  const isStructured =
    isEnabled(FEATURE_FLAGS.PRICED_MODIFIERS) &&
    !stagedProduct.promotionSelections?.length &&
    !hasPositiveWeight(stagedProduct.weightKg);

  const category = stagedProduct.product.productCategory;
  const showFood = category?.printInKitchen;
  const showDrink = category?.printInBar;
  
  // As agreed, if both are false/undefined, we show all modifiers as fallback
  const fallbackToAll = !showFood && !showDrink;

  const visibleModifiers = productModifiers.filter((m) => {
    if (fallbackToAll) return true;
    if (showFood && showDrink) return true;
    if (showFood && m.type === 'Food') return true;
    if (showDrink && m.type === 'Drink') return true;
    if ((showFood || showDrink) && m.type === 'Both') return true;
    return false;
  });

  const categoryNavReady = !categoriesLoading && !categoriesError;
  const visibleCategoryIds = new Set(
    visibleModifiers.map((modifier) => modifier.categoryId).filter((categoryId): categoryId is string => Boolean(categoryId))
  );
  const pickerCategories = categories
    .filter((category) => visibleCategoryIds.has(category.id))
    .sort((a, b) => a.name.localeCompare(b.name));
  const hasUncategorized = visibleModifiers.some((modifier) => !modifier.categoryId);
  const defaultCategoryId = pickerCategories[0]?.id ?? (hasUncategorized ? UNCATEGORIZED_CATEGORY_ID : null);
  const selectedCategoryIsVisible =
    selectedCategoryId === UNCATEGORIZED_CATEGORY_ID
      ? hasUncategorized
      : pickerCategories.some((category) => category.id === selectedCategoryId);
  const activeCategoryId =
    categoryNavReady && selectedCategoryId && selectedCategoryIsVisible ? selectedCategoryId : defaultCategoryId;
  const shownModifiers = categoryNavReady
    ? visibleModifiers.filter((modifier) => {
        if (activeCategoryId === UNCATEGORIZED_CATEGORY_ID) return !modifier.categoryId;
        if (!activeCategoryId) return true;
        return modifier.categoryId === activeCategoryId;
      })
    : visibleModifiers;
  const showCategoryNav = categoryNavReady && visibleModifiers.length > 0 && (pickerCategories.length > 0 || hasUncategorized);

  const appendModifierText = (modifierName: string) => {
    setLocalNotes((prev) => {
      const trimmed = prev.trimEnd();
      const newLine = trimmed.length > 0 ? '\n' : '';
      return `${trimmed}${newLine}● ${modifierName}\n`;
    });
    
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      }
    }, 0);
  };

  const handleModifierClick = (modifier: ProductModifier) => {
    if (!isStructured) {
      appendModifierText(modifier.name);
      return;
    }
    setLocalModifiers((prev) =>
      toggleSelectedModifier(prev, {
        modifierId: modifier.id,
        name: modifier.name,
        price: modifier.price,
      })
    );
  };

  const isSelected = (modifierId: string) =>
    isStructured && localModifiers.some((entry) => entry.modifierId === modifierId);

  const unitPrice = lineUnitPrice(stagedProduct.product, localModifiers);

  const handleSave = () => {
    onSave(
      stagedProduct.lineId,
      localNotes.trim(),
      isStructured ? localModifiers : undefined
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-none h-[calc(100dvh-1rem)] md:max-w-3xl md:h-auto md:max-h-[calc(100dvh-1rem)] flex flex-col p-4 lg:p-6 overflow-hidden">
        <DialogHeader className="pb-3 border-b shrink-0 space-y-1 pt-2">
          <DialogTitle>{stagedProduct.product.name} - {translator('Global.notes')}</DialogTitle>
          {isStructured && (
            <DialogDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>{translator('ProductsCatalog.modifiersHint')}</span>
              <span className="font-medium text-foreground tabular-nums">
                {formatAmount(showIvaPrice ? getPriceWithIva(unitPrice) : unitPrice)}
                {showIvaPrice ? ` ${translator('Global.ivaIncluded')}` : ''}
              </span>
            </DialogDescription>
          )}
        </DialogHeader>
        
        <div className="flex flex-col flex-1 min-h-0 pt-4 overflow-hidden">
          {showCategoryNav && activeCategoryId && (
            <Tabs
              value={activeCategoryId}
              onValueChange={setSelectedCategoryId}
              className="mb-3 min-w-0 shrink-0"
            >
              <TabsList className="h-auto w-full justify-start overflow-x-auto">
                {pickerCategories.map((category) => (
                  <TabsTrigger key={category.id} value={category.id} className="shrink-0">
                    {category.name}
                  </TabsTrigger>
                ))}
                {hasUncategorized && (
                  <TabsTrigger value={UNCATEGORIZED_CATEGORY_ID} className="shrink-0">
                    {translator('Products.modifierCategoryUncategorized')}
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          )}
          {shownModifiers.length > 0 && (
            <div className="flex flex-wrap content-start gap-3 flex-1 min-h-0 overflow-y-auto pr-2 pl-2 -mx-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50 mb-4">
              {shownModifiers.map((modifier) => (
                <Button
                  key={modifier.id}
                  variant={isSelected(modifier.id) ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleModifierClick(modifier)}
                  aria-pressed={isStructured ? isSelected(modifier.id) : undefined}
                  className="whitespace-nowrap"
                >
                  {modifier.name}
                  {isStructured && modifier.price > 0 && (
                    <span className="ml-1 tabular-nums opacity-80">
                      +{formatAmount(showIvaPrice ? getPriceWithIva(modifier.price) : modifier.price)}
                      {showIvaPrice ? ` ${translator('Global.ivaIncluded')}` : ''}
                    </span>
                  )}
                </Button>
              ))}
            </div>
          )}
          
          <div className="shrink-0 mt-auto">
            <Textarea
              ref={textareaRef}
              value={localNotes}
              onChange={(e) => setLocalNotes(e.target.value)}
              placeholder={translator('Global.notes')}
              className="h-32 resize-none"
            />
          </div>
        </div>
        
        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-0 shrink-0 pt-4 mt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto sm:mr-2">
            {translator('Global.cancel')}
          </Button>
          <Button onClick={handleSave} className="w-full sm:w-auto">
            {translator('Global.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
